import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from "firebase/firestore";

// Configuración de ponderación para evaluaciones 180°
const PONDERACION_CONFIG = {
  autoevaluacion: 0.4,    // 40% peso de la autoevaluación
  supervisor: 0.6         // 60% peso de la evaluación del supervisor
};

/**
 * Función para calcular el promedio de calificaciones de una evaluación
 * @param {Object} respuestas - Objeto con las respuestas de la evaluación
 * @returns {number} - Promedio de las calificaciones
 */
const calcularPromedioCalificaciones = (respuestas) => {
  const calificaciones = [];
  
  for (const [key, value] of Object.entries(respuestas)) {
    if (value && typeof value.calificacion === 'number' && value.calificacion >= 1 && value.calificacion <= 5) {
      calificaciones.push(value.calificacion);
    }
  }
  
  if (calificaciones.length === 0) {
    return 0;
  }
  
  const suma = calificaciones.reduce((acc, calif) => acc + calif, 0);
  return suma / calificaciones.length;
};

/**
 * Función para obtener las evaluaciones completadas de un empleado
 * @param {string} empleadoCedula - Cédula del empleado
 * @returns {Object} - Objeto con autoevaluación y evaluación de supervisor
 */
const obtenerEvaluacionesEmpleado = async (empleadoCedula) => {
  try {
    // Buscar autoevaluación
    const autoevaluacionQuery = query(
      collection(db, 'autoevaluaciones_realizadas'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const autoevaluacionSnapshot = await getDocs(autoevaluacionQuery);
    
    // Buscar evaluación del supervisor
    const supervisorQuery = query(
      collection(db, 'evaluaciones_realizadas'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const supervisorSnapshot = await getDocs(supervisorQuery);
    
    const autoevaluacion = autoevaluacionSnapshot.empty ? null : {
      id: autoevaluacionSnapshot.docs[0].id,
      ...autoevaluacionSnapshot.docs[0].data()
    };
    
    const evaluacionSupervisor = supervisorSnapshot.empty ? null : {
      id: supervisorSnapshot.docs[0].id,
      ...supervisorSnapshot.docs[0].data()
    };
    
    return { autoevaluacion, evaluacionSupervisor };
  } catch (error) {
    console.error('Error obteniendo evaluaciones del empleado:', error);
    return { autoevaluacion: null, evaluacionSupervisor: null };
  }
};

/**
 * Función para procesar automáticamente una evaluación 180° terminada
 * @param {string} empleadoCedula - Cédula del empleado
 * @returns {boolean} - True si se procesó exitosamente
 */
const procesarEvaluacion180Automatica = async (empleadoCedula) => {
  try {
    console.log(`🚀 Procesando evaluación 180° automática para empleado: ${empleadoCedula}`);
    
    // 1. Obtener las evaluaciones del empleado
    const { autoevaluacion, evaluacionSupervisor } = await obtenerEvaluacionesEmpleado(empleadoCedula);
    
    if (!autoevaluacion || !evaluacionSupervisor) {
      console.log(`❌ Empleado ${empleadoCedula} no tiene ambas evaluaciones completadas:`);
      console.log(`   - Autoevaluación: ${autoevaluacion ? '✅' : '❌'}`);
      console.log(`   - Evaluación supervisor: ${evaluacionSupervisor ? '✅' : '❌'}`);
      return false;
    }
    
    // 2. Calcular promedios individuales
    const promedioAutoevaluacion = calcularPromedioCalificaciones(autoevaluacion.respuestas);
    const promedioSupervisor = calcularPromedioCalificaciones(evaluacionSupervisor.respuestas);
    
    console.log(`📊 Promedios calculados:`);
    console.log(`   - Autoevaluación: ${promedioAutoevaluacion.toFixed(2)}`);
    console.log(`   - Evaluación supervisor: ${promedioSupervisor.toFixed(2)}`);
    
    // 3. Calcular ponderado final
    const ponderadoFinal = (promedioAutoevaluacion * PONDERACION_CONFIG.autoevaluacion) + 
                          (promedioSupervisor * PONDERACION_CONFIG.supervisor);
    
    console.log(`🎯 Ponderado final: ${ponderadoFinal.toFixed(2)}`);
    
    // 4. Obtener información del empleado
    const empleadoQuery = query(
      collection(db, 'empleados'),
      where('cedula', '==', empleadoCedula)
    );
    const empleadoSnapshot = await getDocs(empleadoQuery);
    
    const infoEmpleado = empleadoSnapshot.empty ? {} : empleadoSnapshot.docs[0].data();
    
    // 5. Verificar si ya existe un registro para este empleado
    const existeQuery = query(
      collection(db, 'eval180_terminada'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const existeSnapshot = await getDocs(existeQuery);
    
    // 6. Preparar los datos del documento
    const documentoEval180 = {
      empleado_cedula: empleadoCedula,
      empleado_nombre: infoEmpleado?.nombre || autoevaluacion.empleado_nombre || 'No especificado',
      empleado_cargo: infoEmpleado?.cargo || 'No especificado',
      empleado_empresa: infoEmpleado?.empresa || 'No especificado',
      
      // Información de la evaluación
      plantilla_id: autoevaluacion.plantilla_id || evaluacionSupervisor.plantilla_id,
      evaluacion_nombre: autoevaluacion.evaluacion_nombre || evaluacionSupervisor.evaluacion_nombre || 'Evaluación 360°',
      
      // Promedios individuales
      promedio_autoevaluacion: parseFloat(promedioAutoevaluacion.toFixed(2)),
      promedio_supervisor: parseFloat(promedioSupervisor.toFixed(2)),
      
      // Ponderado final
      ponderado_final: parseFloat(ponderadoFinal.toFixed(2)),
      
      // Configuración de ponderación usada
      ponderacion_config: PONDERACION_CONFIG,
      
      // Referencias a evaluaciones originales
      autoevaluacion_id: autoevaluacion.id,
      evaluacion_supervisor_id: evaluacionSupervisor.id,
      
      // Información del evaluador (supervisor)
      evaluador_cedula: evaluacionSupervisor.evaluador_cedula,
      evaluador_nombre: evaluacionSupervisor.evaluador_nombre,
      
      // Fechas
      fecha_autoevaluacion: autoevaluacion.fecha_finalizacion,
      fecha_evaluacion_supervisor: evaluacionSupervisor.fecha_finalizacion,
      fecha_procesamiento: new Date().toISOString(),
      
      // Estado
      estado: 'procesada',
      version: '1.0'
    };
    
    // 7. Guardar o actualizar el documento
    if (!existeSnapshot.empty) {
      // Actualizar documento existente
      const docId = existeSnapshot.docs[0].id;
      await updateDoc(doc(db, 'eval180_terminada', docId), documentoEval180);
      console.log(`✅ Evaluación 180° actualizada para empleado: ${empleadoCedula} (ID: ${docId})`);
    } else {
      // Crear nuevo documento
      const docRef = await addDoc(collection(db, 'eval180_terminada'), documentoEval180);
      console.log(`✅ Nueva evaluación 180° creada para empleado: ${empleadoCedula} (ID: ${docRef.id})`);
    }
    
    return true;
    
  } catch (error) {
    console.error(`❌ Error procesando evaluación 180° para empleado ${empleadoCedula}:`, error);
    return false;
  }
};

const FormularioAutoevaluacion = ({ evaluacion, onVolver }) => {
  const [userData, setUserData] = useState(null);
  const [empleadoCompleto, setEmpleadoCompleto] = useState(null); // Información completa del empleado desde la tabla empleados
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isEvaluado, setIsEvaluado] = useState(false);
  const [userStatusLoaded, setUserStatusLoaded] = useState(false);
  const [plantilla, setPlantilla] = useState(null);
  const [preguntasPorCompetencia, setPreguntasPorCompetencia] = useState({});
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progreso, setProgreso] = useState(0);

  // Función helper para generar nombre completo con debugging
  const obtenerNombreCompleto = (empleadoCompleto, userData, contexto = "") => {
    console.log(`🔍 DEBUG ${contexto} - obtenerNombreCompleto:`);
    console.log("   - empleadoCompleto:", empleadoCompleto);
    console.log("   - userData:", userData);
    
    if (empleadoCompleto) {
      const nombres = empleadoCompleto.nombres || empleadoCompleto.nombre || '';
      const apellidos = empleadoCompleto.apellidos || empleadoCompleto.apellido || '';
      const nombreCompleto = `${nombres} ${apellidos}`.trim();
      
      console.log(`   - nombres: "${nombres}"`);
      console.log(`   - apellidos: "${apellidos}"`);
      console.log(`   - nombreCompleto: "${nombreCompleto}"`);
      
      return nombreCompleto || userData?.nombre || userData?.nombres || 'No especificado';
    } else {
      const fallback = userData?.nombre || userData?.nombres || 'No especificado';
      console.log(`   - usando fallback: "${fallback}"`);
      return fallback;
    }
  };

  useEffect(() => {
    console.log("📝 FormularioAutoevaluacion montado.");
    const loadEmpleadoDataFromSession = () => {
      const empleadoSession = localStorage.getItem('empleadoSession');
      if (empleadoSession) {
        const data = JSON.parse(empleadoSession);
        console.log("👤 Datos de sesión cargados:", data);
        setUserData(data);
      } else {
        console.error("❌ No se encontraron datos de sesión del empleado.");
        setLoading(false);
      }
    };
    loadEmpleadoDataFromSession();
  }, []);

  // Cargar información completa del empleado desde la colección empleados
  useEffect(() => {
    const loadEmpleadoCompleto = async () => {
      if (!userData || !userData.cedula) return;

      try {
        console.log(`🔍 Cargando información completa del empleado: ${userData.cedula}`);
        const qEmpleado = query(collection(db, "empleados"), where("cedula", "==", userData.cedula));
        const empleadoSnapshot = await getDocs(qEmpleado);
        
        if (!empleadoSnapshot.empty) {
          const empleadoData = empleadoSnapshot.docs[0].data();
          console.log("👤 Información completa del empleado cargada:", empleadoData);
          console.log("🔍 DEBUG - Campos del empleado:");
          console.log("   - nombres:", empleadoData.nombres);
          console.log("   - nombre:", empleadoData.nombre);
          console.log("   - apellidos:", empleadoData.apellidos);
          console.log("   - apellido:", empleadoData.apellido);
          console.log("   - Todos los campos:", Object.keys(empleadoData));
          setEmpleadoCompleto(empleadoData);
        } else {
          console.warn("⚠️ No se encontró información completa del empleado en la colección empleados");
          setEmpleadoCompleto(null);
        }
      } catch (error) {
        console.error("❌ Error cargando información completa del empleado:", error);
        setEmpleadoCompleto(null);
      }
    };

    loadEmpleadoCompleto();
  }, [userData]);

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!userData || !userData.cedula) return;

      // Resetear el estado de carga al inicio
      setUserStatusLoaded(false);

      try {
        console.log(`🔍 Verificando estado del usuario: ${userData.nombre} (${userData.cedula})`);
        console.log(`👤 Cargo del usuario: "${userData.cargo}"`);
        
        // 1. Verificar si el usuario es supervisor de alguien
        const qSupervisor = query(collection(db, "empleados"), where("supervisor_cedula", "==", userData.cedula));
        const supervisorSnapshot = await getDocs(qSupervisor);
        const esSupervisor = !supervisorSnapshot.empty;
        setIsSupervisor(esSupervisor);
        console.log(`👤 ¿Es supervisor? ${esSupervisor} (supervisa a ${supervisorSnapshot.size} empleados)`);

        // 2. Verificar si el usuario es evaluado por alguien (tiene un supervisor asignado)
        const qEvaluado = query(collection(db, "empleados"), where("cedula", "==", userData.cedula));
        const evaluadoSnapshot = await getDocs(qEvaluado);
        let esEvaluado = false;
        let supervisorCedula = null;
        if (!evaluadoSnapshot.empty) {
          const empleadoDoc = evaluadoSnapshot.docs[0].data();
          supervisorCedula = empleadoDoc.supervisor_cedula;
          esEvaluado = !!supervisorCedula;
        }
        setIsEvaluado(esEvaluado);
        console.log(`👤 ¿Es evaluado? ${esEvaluado} (supervisor_cedula: ${supervisorCedula})`);
        
        console.log(`🎯 Resumen del estado: supervisor=${esSupervisor}, evaluado=${esEvaluado} → Liderazgo=${esSupervisor && esEvaluado}`);
        
        // Marcar que el estado del usuario ha sido cargado
        setUserStatusLoaded(true);

      } catch (error) {
        console.error("❌ Error verificando el estado del usuario:", error);
        setUserStatusLoaded(true); // Marcar como cargado incluso si hay error
      }
    };

    checkUserStatus();
  }, [userData]);

  useEffect(() => {
    if (evaluacion && userData && userStatusLoaded) {
      console.log("🚀 Cargando datos iniciales para la evaluación:", evaluacion.id);
      loadInitialData();
    }
  }, [evaluacion, userData, userStatusLoaded]);

  // Función para adaptar la estructura de la plantilla si es necesario
  const adaptarEstructuraPlantilla = async (plantillaData) => {
    console.log(`🔄 Adaptando plantilla: ${plantillaData.id}`);
    if (plantillaData.preguntas && Array.isArray(plantillaData.preguntas)) {
        console.log('✅ Plantilla ya tiene formato con preguntas directas.');
        // Agrupar por un nombre de competencia genérico si no existen
        return { 'Competencias Generales': plantillaData.preguntas };
    }

    if (plantillaData.competenciasDetalle && plantillaData.competenciasSeleccionadas) {
        console.log('🔍 Adaptando desde formato nuevo (competenciasDetalle) con filtrado dinámico.');
        const preguntasAgrupadas = {};
        const competenciasInfo = {};

        const uniqueCompetenciaIds = [...new Set(plantillaData.competenciasSeleccionadas)];

        if (uniqueCompetenciaIds.length > 0) {
            // Dividir en lotes para evitar el límite de Firebase de 30 elementos en consultas 'IN'
            const BATCH_SIZE = 10;
            const competenciasBatches = [];
            
            for (let i = 0; i < uniqueCompetenciaIds.length; i += BATCH_SIZE) {
                competenciasBatches.push(uniqueCompetenciaIds.slice(i, i + BATCH_SIZE));
            }
            
            console.log(`📦 Dividiendo ${uniqueCompetenciaIds.length} competencias en ${competenciasBatches.length} lotes`);
            
            // Ejecutar consultas por lotes
            for (const batch of competenciasBatches) {
                const q = query(collection(db, "competencias"), where("__name__", "in", batch));
                const competenciasSnapshot = await getDocs(q);
                competenciasSnapshot.forEach(doc => {
                    competenciasInfo[doc.id] = doc.data();
                });
            }
            
            console.log(`✅ Cargadas ${Object.keys(competenciasInfo).length} competencias en total`);
        }

        uniqueCompetenciaIds.forEach((competenciaId) => {
            const competenciaInfo = competenciasInfo[competenciaId];
            const competenciaDetalle = plantillaData.competenciasDetalle[competenciaId];

            if (competenciaInfo && competenciaDetalle && competenciaDetalle.preguntas) {
                const nombreCompetencia = competenciaInfo.nombre || 'Competencia sin nombre';
                const tipoCompetencia = competenciaInfo.tipoCompetencia;
                
                let incluirCompetencia = false;

                // Aplicar reglas de filtrado dinámico CORREGIDAS
                if (tipoCompetencia === 'Comportamental' || tipoCompetencia === 'comportamental') {
                    // Las competencias comportamentales se muestran a todos
                    incluirCompetencia = true;
                    console.log(`✅ Incluyendo competencia Comportamental: ${nombreCompetencia}`);
                } else if (tipoCompetencia === 'Liderazgo' || tipoCompetencia === 'liderazgo') {
                    // CORREGIDO: Incluir liderazgo solo si es supervisor Y evaluado (ambas condiciones)
                    if (isSupervisor && isEvaluado) {
                        incluirCompetencia = true;
                        console.log(`✅ Incluyendo competencia de Liderazgo (es supervisor Y evaluado): ${nombreCompetencia}`);
                    } else {
                        console.log(`❌ Omitiendo competencia de Liderazgo (supervisor: ${isSupervisor}, evaluado: ${isEvaluado} - necesita ambos): ${nombreCompetencia}`);
                    }
                } else if (tipoCompetencia === 'Funcional' || tipoCompetencia === 'funcional') {
                    // CORREGIDO: Múltiples métodos de coincidencia para competencias funcionales
                    const cargoEmpleado = userData.cargo || '';
                    const cargoCompetencia = competenciaInfo.cargoEspecifico || '';
                    const posicionCompetencia = competenciaInfo.posicionEspecifica || '';
                    const nombreCompetencia = competenciaInfo.nombre || '';
                    
                    console.log(`🔍 Comparando cargos para competencia funcional "${nombreCompetencia}":`);
                    console.log(`   - Cargo del empleado: "${cargoEmpleado}"`);
                    console.log(`   - Cargo de la competencia: "${cargoCompetencia}"`);
                    console.log(`   - Posición de la competencia: "${posicionCompetencia}"`);
                    console.log(`   - Nombre de la competencia: "${nombreCompetencia}"`);
                    
                    // Método 1: Coincidencia exacta con cargoEspecifico
                    let coincidenciaExacta = cargoEmpleado && cargoCompetencia && 
                        cargoEmpleado.toLowerCase().trim() === cargoCompetencia.toLowerCase().trim();
                    
                    // Método 2: Coincidencia con posicionEspecifica
                    let coincidenciaPosicion = false;
                    if (!coincidenciaExacta && cargoEmpleado && posicionCompetencia) {
                        coincidenciaPosicion = cargoEmpleado.toLowerCase().trim() === posicionCompetencia.toLowerCase().trim();
                    }
                    
                    // Método 3: Coincidencia por nombre de la competencia (fallback para datos corruptos)
                    let coincidenciaPorNombre = false;
                    if (!coincidenciaExacta && !coincidenciaPosicion && cargoEmpleado && nombreCompetencia) {
                        coincidenciaPorNombre = cargoEmpleado.toLowerCase().trim() === nombreCompetencia.toLowerCase().trim();
                    }
                    
                    console.log(`   - Comparación cargoEspecifico: "${cargoEmpleado.toLowerCase().trim()}" === "${cargoCompetencia.toLowerCase().trim()}" = ${coincidenciaExacta}`);
                    console.log(`   - Comparación posicionEspecifica: "${cargoEmpleado.toLowerCase().trim()}" === "${posicionCompetencia.toLowerCase().trim()}" = ${coincidenciaPosicion}`);
                    console.log(`   - Comparación nombreCompetencia: "${cargoEmpleado.toLowerCase().trim()}" === "${nombreCompetencia.toLowerCase().trim()}" = ${coincidenciaPorNombre}`);
                    
                    if (coincidenciaExacta || coincidenciaPosicion || coincidenciaPorNombre) {
                        incluirCompetencia = true;
                        let tipoCoincidencia = coincidenciaExacta ? 'cargoEspecifico' : 
                                             coincidenciaPosicion ? 'posicionEspecifica' : 'nombreCompetencia';
                        console.log(`✅ Incluyendo competencia Funcional (cargo coincide por ${tipoCoincidencia}): ${nombreCompetencia}`);
                    } else {
                        console.log(`❌ Omitiendo competencia Funcional - Razones:`);
                        if (!cargoEmpleado) console.log(`   - Cargo del empleado vacío o nulo`);
                        if (!cargoCompetencia && !posicionCompetencia && !nombreCompetencia) {
                            console.log(`   - Ningún campo de cargo disponible en la competencia`);
                        }
                        if (cargoEmpleado && (cargoCompetencia || posicionCompetencia || nombreCompetencia)) {
                            console.log(`   - Ninguna coincidencia encontrada:`);
                            console.log(`     * "${cargoEmpleado}" ≠ "${cargoCompetencia}" (cargoEspecifico)`);
                            console.log(`     * "${cargoEmpleado}" ≠ "${posicionCompetencia}" (posicionEspecifica)`);
                            console.log(`     * "${cargoEmpleado}" ≠ "${nombreCompetencia}" (nombreCompetencia)`);
                        }
                    }
                } else {
                    // Si no tiene tipo específico, incluirla por defecto (compatibilidad hacia atrás)
                    incluirCompetencia = true;
                    console.log(`⚠️ Incluyendo competencia sin tipo específico: ${nombreCompetencia} (tipo: ${tipoCompetencia})`);
                }

                if (incluirCompetencia) {
                    // Agrupar por TIPO de competencia manteniendo el nombre individual
                    let grupoCompetencia = '';
                    if (tipoCompetencia === 'Comportamental' || tipoCompetencia === 'comportamental') {
                        grupoCompetencia = '🎯 Competencias Comportamentales';
                    } else if (tipoCompetencia === 'Liderazgo' || tipoCompetencia === 'liderazgo') {
                        grupoCompetencia = '👥 Competencias de Liderazgo (Habilidades Blandas)';
                    } else if (tipoCompetencia === 'Funcional' || tipoCompetencia === 'funcional') {
                        grupoCompetencia = '🛠️ Competencias Funcionales';
                    } else {
                        grupoCompetencia = '📋 Otras Competencias';
                    }
                    
                    if (!preguntasAgrupadas[grupoCompetencia]) {
                        preguntasAgrupadas[grupoCompetencia] = [];
                    }

                    // CORREGIDO: Eliminar preguntas duplicadas usando un Set basado en el texto
                    const preguntasUnicas = [];
                    const textosVistos = new Set();
                    
                    competenciaDetalle.preguntas.forEach((pregunta, pregIndex) => {
                        const textoPregunta = pregunta.primera_persona || pregunta.texto || '';
                        if (!textosVistos.has(textoPregunta.trim().toLowerCase()) && textoPregunta.trim()) {
                            textosVistos.add(textoPregunta.trim().toLowerCase());
                            preguntasUnicas.push({
                                id: `${competenciaId}_${pregIndex}`,
                                texto: textoPregunta,
                                tipo: pregunta.tipo || "escala",
                                descripcion: pregunta.descripcion || '',
                                nombreCompetencia: nombreCompetencia, // Agregamos el nombre de la competencia individual
                                competenciaId: competenciaId
                            });
                        }
                    });

                    console.log(`🔍 Competencia "${nombreCompetencia}" → Grupo "${grupoCompetencia}": ${competenciaDetalle.preguntas.length} preguntas originales → ${preguntasUnicas.length} preguntas únicas`);
                    preguntasAgrupadas[grupoCompetencia].push(...preguntasUnicas);
                }
            } else {
                console.warn(`⚠️ No se encontró la competencia con ID: ${competenciaId}`);
            }
        });
        
        console.log(`✅ Competencias filtradas dinámicamente y agrupadas: ${Object.keys(preguntasAgrupadas).length}`);
        
        // FALLBACK: Si no se incluyó ninguna competencia, incluir al menos las comportamentales
        if (Object.keys(preguntasAgrupadas).length === 0 && uniqueCompetenciaIds.length > 0) {
          console.log('🔄 No se encontraron competencias después del filtrado, aplicando fallback...');
          const grupoFallback = '📋 Competencias Generales (Fallback)';
          
          uniqueCompetenciaIds.forEach((competenciaId) => {
            const competenciaInfo = competenciasInfo[competenciaId];
            const competenciaDetalle = plantillaData.competenciasDetalle[competenciaId];
            
            if (competenciaInfo && competenciaDetalle && competenciaDetalle.preguntas) {
              const nombreCompetencia = competenciaInfo.nombre || 'Competencia sin nombre';
              console.log(`🔄 Aplicando fallback para competencia: ${nombreCompetencia}`);
              
              if (!preguntasAgrupadas[grupoFallback]) {
                preguntasAgrupadas[grupoFallback] = [];
              }
              
              const preguntasUnicas = [];
              const textosVistos = new Set();
              
              competenciaDetalle.preguntas.forEach((pregunta, pregIndex) => {
                const textoPregunta = pregunta.primera_persona || pregunta.texto || '';
                if (!textosVistos.has(textoPregunta.trim().toLowerCase()) && textoPregunta.trim()) {
                  textosVistos.add(textoPregunta.trim().toLowerCase());
                  preguntasUnicas.push({
                    id: `${competenciaId}_${pregIndex}`,
                    texto: textoPregunta,
                    tipo: pregunta.tipo || "escala",
                    descripcion: pregunta.descripcion || '',
                  });
                }
              });
              
              preguntasAgrupadas[grupoFallback].push(...preguntasUnicas);
              console.log(`✅ Fallback aplicado para: ${nombreCompetencia} (${preguntasUnicas.length} preguntas)`);
            }
          });
        }
        
        return preguntasAgrupadas;
    }

    console.log('❌ Formato de plantilla no reconocido.');
    return {};
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      if (evaluacion.plantillaId) {
        console.log("📄 Cargando plantilla ID:", evaluacion.plantillaId);
        const plantillaDoc = await getDoc(doc(db, "plantillas_evaluacion", evaluacion.plantillaId));
        if (plantillaDoc.exists()) {
          const plantillaData = { id: plantillaDoc.id, ...plantillaDoc.data() };
          setPlantilla(plantillaData);
          const preguntasAgrupadas = await adaptarEstructuraPlantilla(plantillaData);
          setPreguntasPorCompetencia(preguntasAgrupadas);
          
          console.log("🔍 Cargando respuestas existentes...");
          await loadRespuestasExistentes(evaluacion.id);
        } else {
          console.error("❌ No se encontró la plantilla de evaluación.");
        }
      } else {
        console.error("❌ La evaluación no tiene un plantillaId.");
      }
    } catch (error) {
      console.error("❌ Error al cargar datos iniciales:", error);
    } finally {
      console.log("✅ Finalizando carga, setLoading a false.");
      setLoading(false);
    }
  };

  const loadRespuestasExistentes = async (evaluacionId) => {
    if (!userData) return;
    try {
      const q = query(
        collection(db, "respuestas_evaluacion"),
        where("evaluacion_id", "==", evaluacionId),
        where("respondido_por_cedula", "==", userData.cedula)
      );
      const querySnapshot = await getDocs(q);
      const respuestasExistentes = {};
      querySnapshot.forEach(doc => {
        const data = doc.data();
        respuestasExistentes[data.pregunta_id] = {
          id: doc.id,
          calificacion: data.calificacion,
          comentario: data.comentario || ""
        };
      });
      console.log("✅ Respuestas existentes cargadas:", Object.keys(respuestasExistentes).length);
      setRespuestas(respuestasExistentes);
    } catch (error) {
      console.error("❌ Error al cargar respuestas existentes:", error);
    }
  };

  useEffect(() => {
    if (preguntasPorCompetencia && Object.keys(preguntasPorCompetencia).length > 0) {
      console.log('🔄 useEffect: Recalculando progreso con nuevas respuestas');
      calcularProgreso(respuestas);
    }
  }, [respuestas, preguntasPorCompetencia]);

  const calcularProgreso = (respuestasActuales) => {
    if (!preguntasPorCompetencia || Object.keys(preguntasPorCompetencia).length === 0) {
        console.log('🔍 calcularProgreso: No hay preguntasPorCompetencia disponibles');
        setProgreso(0);
        return;
    }
    
    // Contar total de preguntas de todas las competencias
    let totalPreguntas = 0;
    Object.keys(preguntasPorCompetencia).forEach(competencia => {
      const preguntas = preguntasPorCompetencia[competencia];
      if (Array.isArray(preguntas)) {
        totalPreguntas += preguntas.length;
      }
    });
    
    console.log(`🔍 calcularProgreso: Total de preguntas = ${totalPreguntas}`);
    
    if (totalPreguntas === 0) {
        console.log('🔍 calcularProgreso: No hay preguntas, progreso 100%');
        setProgreso(100);
        return;
    }
    
    // Contar preguntas respondidas (que tienen calificación)
    const preguntasRespondidas = Object.keys(respuestasActuales).filter(id => {
      const respuesta = respuestasActuales[id];
      const tieneCalificacion = respuesta && respuesta.calificacion !== undefined && respuesta.calificacion !== null && respuesta.calificacion !== '';
      if (tieneCalificacion) {
        console.log(`✅ Pregunta ${id} respondida con calificación: ${respuesta.calificacion}`);
      }
      return tieneCalificacion;
    }).length;
    
    console.log(`🔍 calcularProgreso: Preguntas respondidas = ${preguntasRespondidas} de ${totalPreguntas}`);
    
    const porcentaje = (preguntasRespondidas / totalPreguntas) * 100;
    const progresoFinal = Math.round(porcentaje);
    
    console.log(`🔍 calcularProgreso: Progreso calculado = ${progresoFinal}%`);
    setProgreso(progresoFinal);
  };

  const handleRespuestaChange = (preguntaId, valor) => {
    console.log(`🔄 handleRespuestaChange: Pregunta ${preguntaId}, Valor: ${valor}`);
    
    const esNumero = typeof valor === 'number' && !isNaN(valor);
    const tipo = esNumero ? 'calificacion' : 'comentario';

    setRespuestas(prev => {
      const nuevasRespuestas = {
        ...prev,
        [preguntaId]: {
          ...prev[preguntaId],
          [tipo]: valor
        }
      };
      
      console.log(`🔄 handleRespuestaChange: Nuevas respuestas para pregunta ${preguntaId}:`, nuevasRespuestas[preguntaId]);
      
      // Recalcular progreso inmediatamente
      setTimeout(() => calcularProgreso(nuevasRespuestas), 0);
      
      return nuevasRespuestas;
    });
  };

  const guardarRespuesta = async (preguntaId, competenciaId) => {
    if (!userData) {
        alert("No se pueden guardar respuestas sin datos de usuario.");
        return;
    }
    try {
      setSaving(true);
      const respuesta = respuestas[preguntaId];
      if (!respuesta || !respuesta.calificacion) {
        alert("Por favor selecciona una calificación antes de guardar.");
        setSaving(false);
        return;
      }
      const respuestaData = {
        evaluacion_id: evaluacion.id,
        pregunta_id: preguntaId,
        competencia_id: competenciaId,
        calificacion: respuesta.calificacion,
        comentario: respuesta.comentario || "",
        respondido_por_cedula: userData.cedula,
        respondido_por_nombre: empleadoCompleto 
          ? `${empleadoCompleto.nombres || empleadoCompleto.nombre || ''} ${empleadoCompleto.apellidos || ''}`.trim() || userData.nombre
          : userData.nombre,
        tipo_evaluacion: "autoevaluacion",
        fecha_respuesta: new Date().toISOString()
      };
      if (respuesta.id) {
        await updateDoc(doc(db, "respuestas_evaluacion", respuesta.id), respuestaData);
      } else {
        const docRef = await addDoc(collection(db, "respuestas_evaluacion"), respuestaData);
        setRespuestas(prev => ({ ...prev, [preguntaId]: { ...prev[preguntaId], id: docRef.id } }));
      }
      await actualizarProgresoEvaluacion();
      alert("Respuesta guardada exitosamente");
    } catch (error) {
      console.error("Error al guardar respuesta:", error);
      alert("Error al guardar la respuesta");
    } finally {
      setSaving(false);
    }
  };

  const actualizarProgresoEvaluacion = async () => {
    try {
        const totalPreguntas = plantilla.competencias.reduce((total, comp) => total + (comp.preguntas ? comp.preguntas.length : 0), 0);
        const preguntasRespondidas = Object.keys(respuestas).filter(id => respuestas[id] && respuestas[id].calificacion).length;
        const porcentajeCompletado = totalPreguntas > 0 ? (preguntasRespondidas / totalPreguntas) * 100 : 0;
      
      await updateDoc(doc(db, "evaluaciones_asignadas", evaluacion.id), {
        respuestas_completadas: preguntasRespondidas,
        total_preguntas: totalPreguntas,
        porcentaje_completado: Math.round(porcentajeCompletado),
        estado: porcentajeCompletado >= 100 ? "completada" : "en_progreso",
        fecha_ultima_actualizacion: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error al actualizar progreso:", error);
    }
  };

  const finalizarEvaluacion = async () => {
    try {
      console.log('🚀 Iniciando finalización de evaluación...');
      
      // Calcular total de preguntas usando preguntasPorCompetencia
      let totalPreguntas = 0;
      Object.keys(preguntasPorCompetencia).forEach(competencia => {
        const preguntas = preguntasPorCompetencia[competencia];
        if (Array.isArray(preguntas)) {
          totalPreguntas += preguntas.length;
        }
      });
      
      console.log(`🔍 Total de preguntas: ${totalPreguntas}`);
      
      // Contar preguntas respondidas
      const preguntasRespondidas = Object.keys(respuestas).filter(id => {
        const respuesta = respuestas[id];
        const tieneCalificacion = respuesta && respuesta.calificacion !== undefined && respuesta.calificacion !== null && respuesta.calificacion !== '';
        return tieneCalificacion;
      }).length;
      
      console.log(`🔍 Preguntas respondidas: ${preguntasRespondidas}`);
      
      if (preguntasRespondidas < totalPreguntas) {
        alert(`Debes completar todas las preguntas. Te faltan ${totalPreguntas - preguntasRespondidas} preguntas.`);
        return;
      }
      
      console.log('💾 Actualizando estado de evaluación en la base de datos...');
      await updateDoc(doc(db, "evaluaciones_asignadas", evaluacion.id), {
        estado: "completada",
        fecha_finalizacion: new Date().toISOString(),
        porcentaje_completado: 100,
        respuestas: respuestas, // Guardar el mapa de respuestas directamente
      });

      // Crear documento en la nueva colección 'autoevaluaciones_realizadas'
      console.log('📝 Creando registro en autoevaluaciones_realizadas...');
      await addDoc(collection(db, "autoevaluaciones_realizadas"), {
        evaluacion_id: evaluacion.id,
        plantilla_id: evaluacion.plantillaId,
        empleado_cedula: userData.cedula,
        empleado_nombre: empleadoCompleto 
          ? `${empleadoCompleto.nombres || empleadoCompleto.nombre || ''} ${empleadoCompleto.apellidos || ''}`.trim() || userData.nombre
          : userData.nombre,
        respuestas: respuestas,
        fecha_finalizacion: new Date().toISOString(),
      });
      
      // NUEVO: Procesar automáticamente la evaluación 180° si están completas ambas evaluaciones
      console.log('🔄 Verificando si se puede procesar la evaluación 180° automáticamente...');
      try {
        const procesado = await procesarEvaluacion180Automatica(userData.cedula);
        if (procesado) {
          console.log('🎉 ¡Evaluación 180° procesada automáticamente!');
        } else {
          console.log('📋 Evaluación 180° no procesada: faltan evaluaciones por completar.');
        }
      } catch (error) {
        console.error('❌ Error en procesamiento automático de evaluación 180°:', error);
        // No interrumpir el flujo principal si falla el procesamiento automático
      }
      
      console.log('✅ Evaluación finalizada exitosamente');
      alert("¡Autoevaluación completada exitosamente!");
      onVolver();
    } catch (error) {
      console.error("❌ Error al finalizar evaluación:", error);
      alert("Error al finalizar la evaluación: " + error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #1e40af', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
        <p>Cargando formulario de autoevaluación...</p>
      </div>
    );
  }

  if (!evaluacion || !plantilla) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
        <h2>Evaluación no encontrada</h2>
        <p>No se pudo cargar la información de la evaluación. Contacta al administrador.</p>
        <button onClick={onVolver} style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '6px', cursor: 'pointer', marginTop: '1rem' }}>
          Volver al Dashboard
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>🎯 Autoevaluación</h1>
          <button onClick={onVolver} style={{ backgroundColor: '#6b7280', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer' }}>
            ← Volver
          </button>
        </div>

        {/* Encabezado de Bienvenida */}
        <div style={{ 
          backgroundColor: '#f0f9ff', 
          padding: '2rem', 
          borderRadius: '12px', 
          border: '1px solid #0ea5e9',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            color: '#0c4a6e', 
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            Hola {obtenerNombreCompleto(empleadoCompleto, userData, "SALUDO")}, es momento de tu autoevaluación 👋
          </h2>
          
          {/* Información del Empleado */}
          <div style={{ 
            backgroundColor: '#e0f2fe', 
            padding: '1rem', 
            borderRadius: '8px',
            border: '1px solid #0891b2',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#0c4a6e', 
              margin: '0 0 0.5rem 0'
            }}>
              📋 Información del Empleado
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
              <div>
                <strong style={{ color: '#0c4a6e' }}>Nombre:</strong> {obtenerNombreCompleto(empleadoCompleto, userData, "INFO_EMPLEADO")}
              </div>
              <div>
                <strong style={{ color: '#0c4a6e' }}>Cédula:</strong> {userData?.cedula || 'No especificada'}
              </div>
              <div>
                <strong style={{ color: '#0c4a6e' }}>Cargo:</strong> {empleadoCompleto?.cargo || userData?.cargo || 'No especificado'}
              </div>
            </div>
          </div>
          
          <p style={{ 
            color: '#0c4a6e', 
            margin: '0 0 1.5rem 0', 
            lineHeight: '1.6',
            fontSize: '1rem'
          }}>
            Queremos invitarte a participar en tu autoevaluación de desempeño, una parte importante de nuestro proceso de evaluación 180°. 
            Este es tu espacio para reflexionar sobre tu trabajo, reconocer tus logros e identificar oportunidades para seguir creciendo profesionalmente.
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#0c4a6e', 
              margin: '0 0 0.75rem 0'
            }}>
              ¿Por qué es importante?
            </h3>
            <p style={{ color: '#0c4a6e', margin: '0 0 0.5rem 0', lineHeight: '1.6' }}>
              Esta autoevaluación te permite:
            </p>
            <ul style={{ 
              color: '#0c4a6e', 
              margin: '0 0 0 1.5rem', 
              lineHeight: '1.6',
              paddingLeft: '0.5rem'
            }}>
              <li style={{ marginBottom: '0.25rem' }}>Reflexionar sobre tu trabajo y tomar conciencia de tus fortalezas</li>
              <li style={{ marginBottom: '0.25rem' }}>Identificar aquellas áreas donde puedes crecer y las herramientas que necesitas para lograrlo</li>
              <li style={{ marginBottom: '0.25rem' }}>Participar activamente en tu desarrollo profesional</li>
              <li>Conectar mejor tu trabajo diario con los valores y objetivos que compartimos como organización</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#0c4a6e', 
              margin: '0 0 0.75rem 0'
            }}>
              Algunas sugerencias para aprovechar al máximo este proceso:
            </h3>
            <ul style={{ 
              color: '#0c4a6e', 
              margin: '0 0 0 1.5rem', 
              lineHeight: '1.6',
              paddingLeft: '0.5rem'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Sé auténtico contigo mismo</strong> - La honestidad es tu mejor aliada. Reconoce tanto tus aciertos como los aspectos que puedes mejorar.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Enfócate en el crecimiento</strong> - Además de celebrar lo que haces bien, piensa en aquello que te gustaría desarrollar más.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Ve la retroalimentación como un regalo</strong> - Los comentarios que recibes son oportunidades valiosas para evolucionar en tu carrera.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Propón acciones concretas</strong> - En los espacios de comentarios adicionales, comparte ideas específicas sobre cómo te gustaría desarrollarte. ¿Qué capacitación te interesa? ¿Qué proyectos te motivarían?
              </li>
            </ul>
          </div>

          <div style={{ 
            backgroundColor: '#e0f2fe', 
            padding: '1rem', 
            borderRadius: '8px',
            border: '1px solid #0891b2'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#0c4a6e', 
              margin: '0 0 0.5rem 0'
            }}>
              Recuerda:
            </h3>
            <p style={{ 
              color: '#0c4a6e', 
              margin: '0 0 0.75rem 0', 
              lineHeight: '1.6',
              fontSize: '0.95rem'
            }}>
              Este proceso está diseñado para apoyarte en tu crecimiento profesional. Tu participación honesta y reflexiva 
              nos ayuda a crear juntos un plan de desarrollo que realmente funcione para ti y para la organización.
            </p>
            <p style={{ 
              color: '#0c4a6e', 
              margin: '0', 
              fontWeight: '600',
              fontSize: '0.95rem'
            }}>
              ¡Gracias por tu tiempo y tu compromiso con tu desarrollo profesional!
            </p>
          </div>
        </div>

        <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e40af' }}>{plantilla.nombre}</h3>
          <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>{plantilla.descripcion}</p>
          <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Progreso</span>
              <span style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>{progreso}%</span>
            </div>
            <div style={{ backgroundColor: '#e5e7eb', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
              <div style={{ backgroundColor: progreso >= 100 ? '#10b981' : '#3b82f6', height: '100%', width: `${progreso}%`, transition: 'width 0.3s ease' }}></div>
            </div>
          </div>
        </div>
      </div>
      {Object.keys(preguntasPorCompetencia).length > 0 ? (
        <form onSubmit={(e) => e.preventDefault()}>
          {Object.keys(preguntasPorCompetencia).map((nombreGrupo, compIndex) => {
            // Agrupar preguntas por competencia individual dentro del grupo
            const preguntasPorCompetenciaIndividual = {};
            
            preguntasPorCompetencia[nombreGrupo].forEach(pregunta => {
              const nombreCompetencia = pregunta.nombreCompetencia || 'Competencia sin nombre';
              if (!preguntasPorCompetenciaIndividual[nombreCompetencia]) {
                preguntasPorCompetenciaIndividual[nombreCompetencia] = [];
              }
              preguntasPorCompetenciaIndividual[nombreCompetencia].push(pregunta);
            });

            return (
              <div key={compIndex} style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '1.5rem', borderBottom: '2px solid #dbeafe', paddingBottom: '0.5rem' }}>
                  {nombreGrupo}
                </h2>
                
                {/* Renderizar cada competencia individual dentro del grupo */}
                {Object.keys(preguntasPorCompetenciaIndividual).map((nombreCompetencia, competenciaIndex) => (
                  <div key={competenciaIndex} style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#059669', marginBottom: '1rem', borderBottom: '1px solid #d1fae5', paddingBottom: '0.5rem' }}>
                      📋 {nombreCompetencia}
                    </h3>
                    
                    {preguntasPorCompetenciaIndividual[nombreCompetencia].map((pregunta, pregIndex) => (
                      <div key={pregunta.id || pregIndex} style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                          {pregIndex + 1}. {pregunta.texto}
                        </h4>
                        
                        {pregunta.descripcion && (
                          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                            {pregunta.descripcion}
                          </p>
                        )}

                        {pregunta.tipo === 'escala' ? (
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Calificación:</span>
                            {[1, 2, 3, 4, 5].map(valor => (
                              <label key={valor} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name={`pregunta_${pregunta.id || pregIndex}`}
                                  value={valor}
                                  checked={respuestas[pregunta.id]?.calificacion == valor}
                                  onChange={(e) => handleRespuestaChange(pregunta.id || pregIndex, parseInt(e.target.value))}
                                  style={{ marginRight: '0.25rem' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>{valor}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <textarea
                            value={respuestas[pregunta.id]?.comentario || ''}
                            onChange={(e) => handleRespuestaChange(pregunta.id || pregIndex, e.target.value)}
                            placeholder="Escribe tu respuesta aquí..."
                            style={{
                              width: '100%',
                              minHeight: '100px',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              resize: 'vertical'
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                
                {/* Comentarios adicionales para todo el grupo */}
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Comentarios Adicionales sobre {nombreGrupo}</h4>
                  <textarea
                    value={respuestas[`comentario_${nombreGrupo}`]?.comentario || ''}
                    onChange={(e) => handleRespuestaChange(`comentario_${nombreGrupo}`, e.target.value)}
                    placeholder="Añade comentarios sobre este grupo de competencias..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            );
          })}

          {/* Autorización para Tratamiento de Datos Personales */}
          <div style={{ 
            marginTop: '2rem', 
            padding: '1rem', 
            backgroundColor: '#f8fafc', 
            border: '1px solid #e5e7eb', 
            borderRadius: '8px' 
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '0.5rem' 
            }}>
              📋 Autorización para Tratamiento de Datos Personales – Evaluación de Desempeño
            </h4>
            <p style={{ 
              fontSize: '0.75rem', 
              color: '#6b7280', 
              lineHeight: '1.4',
              margin: '0'
            }}>
              Yo, <strong>{obtenerNombreCompleto(empleadoCompleto, userData, "AUTORIZACION")}</strong>, identificado(a) con cédula No. <strong>{userData?.cedula || '[número]'}</strong>, autorizo de manera libre, previa e informada a <strong>{evaluacion?.empresaNombre || '[nombre de la empresa]'}</strong> para tratar mis datos personales con el fin de aplicar y gestionar mi evaluación de desempeño laboral.
              <br /><br />
              Declaro que he sido informado(a) sobre la finalidad del proceso, la confidencialidad de la información, y mis derechos de conocer, actualizar, rectificar o suprimir mis datos conforme a la Ley 1581 de 2012.
            </p>
          </div>

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={finalizarEvaluacion}
              disabled={progreso < 100}
              style={{ backgroundColor: progreso < 100 ? '#9ca3af' : '#1e40af', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '6px', cursor: progreso < 100 ? 'not-allowed' : 'pointer', fontSize: '1rem', fontWeight: 'bold', width: '100%', maxWidth: '300px' }}
            >
              {progreso < 100 ? `Completado al ${progreso}%` : 'Finalizar y Enviar'}
            </button>
          </div>
        </form>
      ) : (
        <div style={{textAlign: 'center', padding: '2rem', backgroundColor: 'white', borderRadius: '12px'}}>
            <p>Esta evaluación no tiene competencias o preguntas configuradas.</p>
        </div>
      )}
    </div>
  );
};

export default FormularioAutoevaluacion;
